// Piezas comunes de las secciones Bocetta dentro de Horizon.
// El carrito, la cabecera y el pie son los de Horizon: aquí solo se agrega al carrito con
// las acciones estándar de Shopify (así el cajón y el contador de Horizon se actualizan solos).
import { COLORES, ORDEN, VUELTA, RUTAS, pesos } from '@bocetta/datos.js';

const TX = () => (window.BOCETTA && window.BOCETTA.textos) || {};

export const quieto = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Carrito ---------- */

// lineas: [{ id: variantId, quantity }]
export async function agregarAlCarrito(lineas, boton) {
  lineas = lineas.filter((l) => l && l.id);
  if (!lineas.length) return;
  const txt = boton?.innerHTML;
  if (boton) { boton.disabled = true; boton.setAttribute('aria-busy', 'true'); }
  try {
    const acciones = window.Shopify?.actions;
    if (acciones?.updateCart) {
      const r = await acciones.updateCart({ lines: lineas.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity || 1 })) });
      if (r?.userErrors?.length) throw new Error(r.userErrors[0].message);
      await acciones.openCart();
    } else {
      const r = await fetch(RUTAS.agregar, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lineas })
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).description || TX().errorCarrito || 'Something went wrong');
      location.href = RUTAS.carrito;
    }
  } catch (e) {
    if (boton) aviso(boton, e.message || TX().errorCarrito || 'That didn’t go through. Try again.');
  } finally {
    if (boton) { boton.disabled = false; boton.removeAttribute('aria-busy'); boton.innerHTML = txt; }
  }
}

export function lineaLibreta(clave, cant = 1) {
  return { id: COLORES[clave]?.variante, quantity: cant };
}

function aviso(junto, msg) {
  junto.parentElement.querySelector('.error[data-carrito-error]')?.remove();
  const p = document.createElement('p');
  p.className = 'error';
  p.dataset.carritoError = '';
  p.setAttribute('role', 'alert');
  p.textContent = msg;
  junto.after(p);
}

/* ---------- Sellos de confianza ---------- */

const ICONO_CANDADO = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="1"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>';
const ICONO_CAJA = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M3 7l9-4 9 4v10l-9 4-9-4V7z"/><path d="M3 7l9 4 9-4M12 11v10"/></svg>';
export const sellosLinea = `<p class="sellos__linea"><span>${ICONO_CANDADO}Secure checkout, encrypted (SSL)</span><span>${ICONO_CAJA}Tracked shipping worldwide</span></p>`;

/* ---------- Formularios: se envían a Shopify (clientes y contacto) ---------- */

async function enviarAShopify(form, extra = {}) {
  const datos = new FormData(form);
  Object.entries(extra).forEach(([k, v]) => datos.set(k, v));
  if (!datos.has('utf8')) datos.set('utf8', '✓');
  const r = await fetch(form.getAttribute('action') || '/contact', { method: 'POST', body: datos });
  // Si Shopify pide comprobar que no es un robot, se envía normal para mostrar el captcha
  if (/challenge/.test(r.url)) { HTMLFormElement.prototype.submit.call(form); return false; }
  if (!r.ok) throw new Error('No se pudo enviar');
  return true;
}

export function formularioShopify(form, { alEnviar, validar, extra } = {}) {
  if (form.dataset.bocettaForm) return;
  form.dataset.bocettaForm = '';
  form.setAttribute('novalidate', '');
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    form.querySelectorAll('.error').forEach((el) => el.remove());
    form.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
    const errores = validar ? validar(form) : [];
    if (errores.length) {
      errores.forEach(([campo, msg]) => marcarError(campo, msg));
      errores[0][0].focus();
      return;
    }
    const boton = form.querySelector('[type="submit"]');
    const txt = boton.textContent;
    boton.disabled = true;
    boton.textContent = TX().enviando || 'Sending…';
    try {
      const listo = await enviarAShopify(form, typeof extra === 'function' ? extra(form) : extra);
      if (listo) alEnviar && alEnviar(form);
    } catch {
      marcarError(boton, TX().errorEnvio || 'That didn’t go through. Check your connection and try again.');
    } finally {
      boton.disabled = false;
      boton.textContent = txt;
    }
  });
}

function marcarError(campo, msg) {
  const p = document.createElement('p');
  p.className = 'error';
  p.id = (campo.id || 'campo') + '-error';
  p.textContent = msg;
  campo.setAttribute('aria-invalid', 'true');
  campo.setAttribute('aria-describedby', p.id);
  (campo.closest('.campo') || campo.closest('label') || campo.parentElement).appendChild(p);
}

export const correoValido = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
export const validarCorreo = (f) => { const i = f.querySelector('input[type="email"]'); return correoValido(i.value) ? [] : [[i, TX().correoMal || 'That email doesn’t look complete (name@email.com).']]; };

// Suscriptor de Shopify con etiquetas (newsletter, aviso-rosso, quiz-nero…)
export const suscribir = (etiquetas) => (f) => ({
  form_type: 'customer',
  'contact[email]': f.querySelector('input[type="email"]').value.trim(),
  'contact[tags]': etiquetas,
  'contact[accepts_marketing]': 'true'
});

/* ---------- Coming soon: aviso por correo ---------- */
let nAviso = 0;
export function avisoPronto(clave, { claro = true } = {}) {
  const c = COLORES[clave], id = `aviso-${clave}-${++nAviso}`;
  return `
    <form class="aviso boletin campo" action="/contact" method="post" data-aviso="${clave}">
      <p class="aviso__txt" id="${id}-txt">${String(TX().aviso || 'Bocetta [color] is coming soon. Leave your email and we’ll write the day it’s out.').replaceAll('[color]', c.nombre)}</p>
      <div class="boletin__fila">
        <label for="${id}" class="visualmente-oculto">${TX().correo || 'Your email'}</label>
        <input id="${id}" class="control" type="email" placeholder="${TX().correo || 'Your email'}" autocomplete="email" aria-describedby="${id}-txt">
        <button type="submit" class="btn ${claro ? 'btn--claro' : 'btn--tinta'}">${TX().avisar || 'Notify me'}</button>
      </div>
    </form>`;
}
export function activarAvisos(raiz) {
  raiz.querySelectorAll('[data-aviso]').forEach((form) => {
    formularioShopify(form, {
      validar: validarCorreo,
      extra: suscribir(`newsletter, aviso-${form.dataset.aviso}`),
      alEnviar: (f) => { f.outerHTML = `<p class="confirmacion" role="status">${String(TX().avisoOk || 'Done. You’ll hear from us the day Bocetta [color] is out.').replaceAll('[color]', COLORES[f.dataset.aviso].nombre)}</p>`; }
    });
  });
}

/* ---------- Aparición suave al hacer scroll ---------- */

let observador = null;
export function aparecer(raiz) {
  const els = raiz.querySelectorAll('[data-aparece]:not(.visible)');
  if (quieto() || !('IntersectionObserver' in window) || window.Shopify?.designMode) {
    els.forEach((el) => el.classList.add('visible'));
    return;
  }
  observador ??= new IntersectionObserver((entradas) => {
    entradas.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add('visible'); observador.unobserve(e.target); }
    });
  }, { rootMargin: '0px 0px -10% 0px' });
  els.forEach((el) => observador.observe(el));
}

/* ---------- 3D solo cuando hace falta ---------- */

// El visor 3D (three.js + texturas de las hojas) se carga cuando su caja está por entrar a la pantalla,
// no al abrir la página: la portada y el resto del sitio quedan fluidos.
let modulo3d = null;
export function cargar3d() {
  modulo3d ??= import('@bocetta/libreta3d.js');
  return modulo3d;
}
export function alVerse(el, hacer, margen = '300px') {
  if (!('IntersectionObserver' in window)) { hacer(); return; }
  const io = new IntersectionObserver((es) => {
    if (!es.some((e) => e.isIntersecting)) return;
    io.disconnect();
    hacer();
  }, { rootMargin: margen });
  io.observe(el);
}
// Equipos lentos o con ahorro de datos: se quedan las fotos
export const sin3d = () => quieto() || navigator.connection?.saveData || (navigator.hardwareConcurrency || 8) <= 2;

/* ---------- Tu color ---------- */

export function ponerColor(clave, guardar = false) {
  const c = COLORES[clave] || COLORES.blu;
  const raiz = document.documentElement;
  raiz.style.setProperty('--tu-color', c.hex);
  raiz.style.setProperty('--tu-suave', c.suave);
  raiz.dataset.tuColor = c.clave;
  if (guardar) { try { localStorage.setItem('bocetta-tu-color', c.clave); } catch {} }
  document.dispatchEvent(new CustomEvent('bocetta-color', { detail: c.clave }));
}
export function tuColorGuardado() {
  try { return localStorage.getItem('bocetta-tu-color'); } catch { return null; }
}

// Los remates pasan solos por los cuatro colores cada 2 s, fundiéndose (menos en la ficha, que usa el suyo)
let rotando = false;
export function iniciarColor() {
  const guardado = tuColorGuardado();
  ponerColor(VUELTA.includes(guardado) ? guardado : 'blu');
  requestAnimationFrame(() => requestAnimationFrame(() => document.documentElement.classList.add('color-vivo')));
  if (rotando || quieto() || document.querySelector('bocetta-libreta-3d[data-seguir="true"]') || document.querySelector('bocetta-v4-hero') || document.documentElement.hasAttribute('data-bc4-sitio')) return; // v4.1: la portada v4 tiene su propio color; sin cambios globales cada 2 s
  rotando = true;
  setInterval(() => {
    if (document.hidden) return;
    const i = VUELTA.indexOf(document.documentElement.dataset.tuColor);
    ponerColor(VUELTA[(i + 1) % VUELTA.length]);
  }, 2000);
}

export { COLORES, ORDEN, RUTAS, pesos };
